// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
import {
  PaginationState,
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  flexRender,
  getFilteredRowModel,
  getPaginationRowModel,
  RowSelectionState,
  Row,
  SortingState,
  getSortedRowModel,
  OnChangeFn,
  Table,
} from '@tanstack/react-table';
import { Checkbox } from '@alefeducation/ui-elements-library-core';
import ReactTooltip from 'react-tooltip';
import './data-grid.scss';
import Pagination from './Pagination';
import TableSortIcon from './TableSortIcon';
import Skeleton from '../Skeleton/Skeleton';
import NoData from '../NoData/NoData';

interface DataGridCellProps<T> {
  cell: any;
  dataQa: string;
  enableColumnTruncation?: boolean;
  defaultAlign?: string;
}

const DataGridCell = <T,>({
  cell,
  dataQa,
  enableColumnTruncation,
  defaultAlign,
}: DataGridCellProps<T>) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const cellValue = cell.getValue();
  const content = flexRender(cell.column.columnDef.cell, cell.getContext());

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current && containerRef.current) {
        const containerWidth =
          containerRef.current.getBoundingClientRect().width;
        const textWidth = textRef.current.getBoundingClientRect().width;

        const hasTruncation = textWidth > containerWidth;
        setIsTruncated((prev) =>
          prev !== hasTruncation ? hasTruncation : prev,
        );
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [cellValue]);

  const header = cell.column.columnDef.header;
  const headerText = typeof header === 'string' ? header : '';

  const disableTruncation =
    cell.column.columnDef.meta?.disableTruncation ||
    cell.column.id === 'row-functions-column' ||
    cell.column.id === 'actions' ||
    headerText.toLowerCase().includes('actions');

  const textAlign = cell.column.columnDef.meta?.align || defaultAlign;

  if (disableTruncation || !enableColumnTruncation) {
    return (
      <td className="text-bml" key={cell.id} style={{ textAlign }}>
        {content}
      </td>
    );
  }

  const innerContent = (
    <div ref={containerRef} className="truncate-text" style={{ textAlign }}>
      <span ref={textRef}>{content}</span>
    </div>
  );

  return (
    <td
      className="text-bml truncate-cell"
      key={cell.id}
      data-testid={`${dataQa}-cell-${cell.id}`}
    >
      {isTruncated ? (
        <>
          <div data-tip data-for={cell.id}>
            {innerContent}
          </div>
          <ReactTooltip id={cell.id} effect="solid">
            {content}
          </ReactTooltip>
        </>
      ) : (
        innerContent
      )}
    </td>
  );
};

export interface DataGridPropsType<T> {
  data: Array<T>;
  dataQa?: string;
  columns: Array<ColumnDef<T>>;
  pageCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onRowClick?: (row: T) => void;
  isDataLoading: boolean;
  allowPagination: boolean;
  compactPagination?: boolean;
  selectable?: boolean;
  page?: number;
  size?: number;
  onSelectionChange?: (rowSelectionState: RowSelectionState) => void;
  getRowId?: (originalRow: T, index: number, parent?: Row<T>) => string;
  getRowEnableStatus?: (originalRow: T) => boolean;
  selectedRows?: Object;
  sortable?: boolean;
  manualSorting?: boolean;
  onSortingChange?: OnChangeFn<SortingState>;
  defaultSorting?: SortingState;
  showSelectedCount?: boolean;
  enableColumnTruncation?: boolean;
  defaultAlign?: 'left' | 'right' | 'center';
}

const formatPageSize = (size: number, pageSizeOptions: Array<number>) => {
  const largestOption = Math.max(...pageSizeOptions);

  return size < largestOption ? size : largestOption;
};

const rowSelectionColumn = <T,>(dataQa: string) => ({
  id: 'row-select-column',
  size: 66,
  header: ({ table }: { table: Table<T> }) => (
    <Checkbox
      className="alef-data-grid select-all"
      data-testid={`${dataQa}-select-all`}
      checked={table.getIsAllRowsSelected() || table.getIsSomeRowsSelected()}
      onChange={table.getToggleAllRowsSelectedHandler()}
      value=""
      variant={table.getIsSomeRowsSelected() ? 'default-partial' : 'default'}
    />
  ),
  cell: ({ row }: { row: Row<T> }) => (
    <Checkbox
      data-testid={`${dataQa}-select-row`}
      checked={row.getIsSelected()}
      disabled={!row.getCanSelect()}
      onChange={row.getToggleSelectedHandler()}
      onClick={(e) => e.stopPropagation()}
      value=""
      variant={'default'}
    />
  ),
});

const rowsPerPageOptions = [10, 20, 30];
function DataGrid<T>(props: DataGridPropsType<T>) {
  const {
    data,
    dataQa = 'table',
    columns,
    pageCount,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    isDataLoading = false,
    allowPagination = false,
    page,
    size,
    selectable = false,
    onSelectionChange,
    getRowId,
    compactPagination,
    selectedRows,
    sortable,
    manualSorting,
    onSortingChange,
    getRowEnableStatus,
    defaultSorting,
    showSelectedCount,
    enableColumnTruncation = false,
    defaultAlign,
  } = props;

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: page || 1,
    pageSize: size || 10,
  });
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);

  useEffect(() => {
    if (onPageChange) onPageChange(pageIndex);
  }, [pageIndex]);

  useEffect(() => {
    if (onPageSizeChange) onPageSizeChange(pageSize);
  }, [pageSize]);

  useEffect(() => {
    const pageIndex = page || 1;
    const pageSize = size ? formatPageSize(size, rowsPerPageOptions) : 10;
    setPagination({ pageIndex, pageSize });
  }, [page, size]);

  useEffect(() => {
    if (selectable && onSelectionChange) {
      onSelectionChange(rowSelection);
    }
  }, [rowSelection]);

  useEffect(() => {
    if (!selectedRows) {
      setRowSelection({});
    }
  }, [selectedRows]);

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const handleSortingChange = (param) => {
    setSorting(param);
    if (manualSorting) {
      onSortingChange(param());
    }
  };

  const dataGridColumns = selectable
    ? [rowSelectionColumn(dataQa), ...columns]
    : columns;

  const table = useReactTable({
    data: data || [],
    columns: dataGridColumns,
    pageCount: pageCount ?? -1,
    state: {
      pagination,
      rowSelection,
      sorting,
    },
    getRowId: getRowId,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: selectable,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: handleSortingChange,
    enableSorting: sortable,
    manualSorting: manualSorting,
    manualPagination: true,
    defaultColumn: {
      minSize: 0,
      size: Number.MAX_SAFE_INTEGER,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
  });

  const onChangeRowsPerPage = (pageSize: number) => {
    onPageSizeChange(pageSize);
    setPagination({ pageIndex, pageSize });
  };

  const getHeaderTitle = (header) => {
    if (!header.column.getCanSort()) {
      return undefined;
    }

    const nextSortingOrder = header.column.getNextSortingOrder();
    if (nextSortingOrder === 'asc') {
      return 'Sort ascending';
    } else if (nextSortingOrder === 'desc') {
      return 'Sort descending';
    } else {
      return 'Clear sort';
    }
  };
  const rowsSelected = Object.keys(rowSelection).length;

  return isDataLoading ? (
    <Skeleton type="table" />
  ) : (
    <div className="alef-data-grid" data-testid={dataQa}>
      <ReactTooltip
        place="bottom"
        effect="solid"
        multiline
        wrapper="div"
        globalEventOff="hideGlobalToolTip"
      />
      {selectable && showSelectedCount && rowsSelected > 0 && (
        <div className="alef-data-grid__total-selected-count">
          {rowsSelected} rows selected
        </div>
      )}
      <table>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  className="text-bmb"
                  key={header.id}
                  style={{
                    width:
                      header.getSize() === Number.MAX_SAFE_INTEGER
                        ? 'auto'
                        : header.getSize(),
                  }}
                >
                  {header.isPlaceholder ? null : (
                    <button
                      className={
                        header.column.getCanSort()
                          ? 'alef-data-grid__sortable'
                          : 'no-cursor w-full text-left'
                      }
                      onClick={header.column.getToggleSortingHandler()}
                      title={getHeaderTitle(header)}
                    >
                      <div>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>
                      {{
                        asc: <TableSortIcon sort="asc" />,
                        desc: <TableSortIcon sort="desc" />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        {table.getRowModel().rows.length ? (
          <tbody>
            {table.getRowModel().rows.map((row, index) => {
              const isDisabled =
                getRowEnableStatus && !getRowEnableStatus(row.original);
              const disabledClass = isDisabled ? 'row-disabled' : '';
              const stripeClass = index % 2 === 0 ? 'row-even' : 'row-odd';
              return (
                <tr
                  key={row.id}
                  style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                  onClick={() => !!onRowClick && onRowClick(row.original)}
                  className={`${
                    row.getIsSelected() ? 'row-selected' : ''
                  } ${disabledClass} ${stripeClass}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <DataGridCell
                      key={cell.id}
                      cell={cell}
                      dataQa={dataQa}
                      enableColumnTruncation={enableColumnTruncation}
                      defaultAlign={defaultAlign}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
        ) : (
          <tbody className="alef-grid-no-data">
            <tr>
              <td colSpan={table.getVisibleLeafColumns().length}>
                <NoData message="No results found, refine search to show results." />
              </td>
            </tr>
          </tbody>
        )}
      </table>
      <div className="alef-data-grid__pagination">
        {allowPagination && table.getPageCount() > 10 ? (
          <Pagination
            currentPage={pageIndex}
            selectedPageSizeOption={pageSize}
            rowsPerPageOptions={rowsPerPageOptions}
            onSelection={onChangeRowsPerPage}
            totalRecords={table.getPageCount()}
            setPageIndex={table.setPageIndex}
            goToNextPage={() =>
              pageIndex < table.getPageCount() / pageSize && table.nextPage()
            }
            goToPrevPage={() => pageIndex > 1 && table.previousPage()}
            compactPagination={compactPagination}
          />
        ) : null}
      </div>
    </div>
  );
}

DataGrid.defaultProps = {
  sortable: false,
  defaultSorting: [],
  showSelectedCount: false,
};

export default DataGrid;
